import { useState } from 'react'
import { X, Search, Image as ImageIcon } from 'lucide-react'
import type { PageComponent } from '@/types/admin'

interface GalleryModalProps {
  open: boolean
  onClose: () => void
  onSelect: (component: PageComponent) => void
  components: PageComponent[]
  isLoading?: boolean
}

function isImageUrl(preview: string) {
  return preview.startsWith('http://') || preview.startsWith('https://') || preview.startsWith('/')
}

function ComponentPreview({ preview }: { preview?: string | null }) {
  if (!preview) {
    return (
      <div className="h-20 flex items-center justify-center bg-muted/30 rounded-md">
        <ImageIcon className="h-5 w-5 text-muted-foreground/30" />
      </div>
    )
  }
  if (isImageUrl(preview)) {
    return (
      <div className="h-20 overflow-hidden rounded-md bg-muted/30">
        <img src={preview} alt="" className="w-full h-full object-cover" />
      </div>
    )
  }
  return (
    <pre className="text-[9px] leading-tight font-mono overflow-hidden whitespace-pre text-muted-foreground/60 group-hover:text-muted-foreground">
      {preview}
    </pre>
  )
}

export function AdminGalleryModal({ open, onClose, onSelect, components, isLoading }: GalleryModalProps) {
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const [search, setSearch] = useState('')

  if (!open) return null

  const categories = [
    { id: 'all', label: 'Semua' },
    ...Array.from(new Set(components.map((c) => c.category))).map((cat) => ({
      id: cat,
      label: cat,
    })),
  ]

  const filtered = components.filter((c) => {
    const matchCategory = activeCategory === 'all' || c.category === activeCategory
    if (!matchCategory) return false
    if (!search.trim()) return true
    const q = search.toLowerCase()
    return c.label.toLowerCase().includes(q) || c.description.toLowerCase().includes(q)
  })

  function handleSelect(def: PageComponent) {
    onSelect(def)
    onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-background/80 backdrop-blur-sm" />

      <div
        className="relative z-10 w-full max-w-3xl max-h-[85vh] flex flex-col rounded-t-2xl sm:rounded-2xl border border-border bg-card shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 py-4 border-b border-border">
          <h2 className="font-semibold text-base flex-1">Tambah Komponen</h2>
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cari komponen..."
              className="w-full rounded-md border border-input bg-background pl-8 pr-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex gap-1 px-5 py-2 border-b border-border overflow-x-auto">
          {categories.map((cat) => (
            <button
              key={cat.id}
              type="button"
              onClick={() => setActiveCategory(cat.id)}
              className={`shrink-0 rounded-md px-3 py-1 text-xs font-medium transition-colors ${
                activeCategory === cat.id
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted'
              }`}
            >
              {cat.label}
            </button>
          ))}
        </div>

        {/* Component grid */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <p className="text-sm text-muted-foreground text-center py-8">Memuat komponen...</p>
          ) : filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              Tidak ada komponen yang cocok.
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {filtered.map((def) => (
                <button
                  key={def.component_key}
                  type="button"
                  onClick={() => handleSelect(def)}
                  className="text-left rounded-xl border border-border bg-background p-3 space-y-2 transition-colors hover:border-primary hover:bg-primary/5 cursor-pointer group"
                >
                  <ComponentPreview preview={def.preview} />
                  <div>
                    <p className="text-xs font-semibold text-foreground leading-tight">{def.label}</p>
                    {def.data_requirement && (
                      <span className="inline-block mt-0.5 text-[9px] uppercase tracking-wide bg-primary/10 text-primary px-1.5 py-0.5 rounded font-medium">
                        {def.data_requirement}
                      </span>
                    )}
                    <p className="mt-1 text-[10px] text-muted-foreground line-clamp-2 leading-tight">
                      {def.description}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
